import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Temporary fallback for testing
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SPAx6EK3udVsnBxzcoHF8b5EtGBk1pImNIYvDBqqsmpRPIoEAoN3YLi8gl0oiSMrFIAFwFNsoxz3Dsw0DVSbJtV006A5IGZ12';

console.log('Environment check:', {
  nodeEnv: process.env.NODE_ENV,
  stripeKey: stripePublishableKey ? 'Found' : 'Missing',
  allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
});

if (!stripePublishableKey) {
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not defined in environment variables');
  console.error('Available REACT_APP_ variables:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Initialize Firebase Functions
const functions = getFunctions(getApp());

// Stripe Connect Firebase Functions
export const createConnectAccount = async (userEmail, firstName, lastName) => {
  try {
    console.log('Creating Connect account with data:', {
      email: userEmail,
      firstName,
      lastName,
      origin: window.location.origin
    });
    
    const createConnectAccountFn = httpsCallable(functions, 'createConnectAccount');
    
    const result = await createConnectAccountFn({
      email: userEmail,
      firstName,
      lastName,
      origin: window.location.origin
    });

    console.log('Firebase Function result:', result);
    return result.data;
  } catch (error) {
    console.error('Error creating Connect account:', error);
    console.error('Firebase Function error details:', {
      code: error.code,
      message: error.message,
      details: error.details,
      customData: error.customData
    });
    throw error;
  }
};

export const getConnectAccountStatus = async (accountId) => {
  try {
    const getConnectAccountStatusFn = httpsCallable(functions, 'getConnectAccountStatus');
    
    const result = await getConnectAccountStatusFn({
      accountId
    });

    return result.data;
  } catch (error) {
    console.error('Error getting Connect account status:', error);
    throw error;
  }
};

export const createAccountLink = async (accountId, refreshUrl, returnUrl) => {
  try {
    const createAccountLinkFn = httpsCallable(functions, 'createAccountLink');
    
    const result = await createAccountLinkFn({
      accountId,
      refreshUrl,
      returnUrl
    });

    return result.data.url;
  } catch (error) {
    console.error('Error creating account link:', error);
    throw error;
  }
};

export const createDashboardLink = async (accountId) => {
  try {
    const createDashboardLinkFn = httpsCallable(functions, 'createDashboardLink');
    
    const result = await createDashboardLinkFn({
      accountId
    });

    return result.data.url;
  } catch (error) {
    console.error('Error creating dashboard link:', error);
    throw error;
  }
};

export const updateConnectAccount = async (accountId, accountData) => {
  try {
    const updateConnectAccountFn = httpsCallable(functions, 'updateConnectAccount');
    
    const result = await updateConnectAccountFn({
      accountId,
      accountData
    });

    return result.data;
  } catch (error) {
    console.error('Error updating Connect account:', error);
    throw error;
  }
};

export const getProviderEarnings = async (accountId, startDate = null, endDate = null) => {
  try {
    const getProviderEarningsFn = httpsCallable(functions, 'getProviderEarnings');
    
    const result = await getProviderEarningsFn({
      accountId,
      startDate,
      endDate
    });

    return result.data;
  } catch (error) {
    console.error('Error getting provider earnings:', error);
    throw error;
  }
};

export const createSessionPayment = async (amount, providerId, sessionDetails) => {
  try {
    const createSessionPaymentFn = httpsCallable(functions, 'createSessionPayment');
    
    const result = await createSessionPaymentFn({
      amount,
      providerId,
      sessionDetails
    });

    return result.data;
  } catch (error) {
    console.error('Error creating session payment:', error);
    throw error;
  }
};

export const completeSessionAndTransfer = async (sessionId, providerAccountId) => {
  try {
    const completeSessionAndTransferFn = httpsCallable(functions, 'completeSessionAndTransfer');
    
    const result = await completeSessionAndTransferFn({
      sessionId,
      providerAccountId
    });

    return result.data;
  } catch (error) {
    console.error('Error completing session transfer:', error);
    throw error;
  }
};

// New Stripe Checkout functions
export const createCheckoutSession = async (amount, providerId, providerName, sessionDetails, successUrl, cancelUrl) => {
  try {
    const createCheckoutSessionFn = httpsCallable(functions, 'createCheckoutSession');
    
    const result = await createCheckoutSessionFn({
      amount,
      providerId,
      providerName,
      sessionDetails,
      successUrl,
      cancelUrl
    });

    return result.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

export const completeCheckoutSession = async (checkoutSessionId) => {
  try {
    const completeCheckoutSessionFn = httpsCallable(functions, 'completeCheckoutSession');
    
    const result = await completeCheckoutSessionFn({
      checkoutSessionId
    });

    return result.data;
  } catch (error) {
    console.error('Error completing checkout session:', error);
    throw error;
  }
};

export const updateBankAccount = async (accountId, bankAccountDetails) => {
  try {
    const updateBankAccountFn = httpsCallable(functions, 'updateBankAccount');
    
    const result = await updateBankAccountFn({
      accountId,
      bankAccountDetails
    });

    return result.data;
  } catch (error) {
    console.error('Error updating bank account:', error);
    throw error;
  }
};

export const confirmSession = async (sessionId) => {
  try {
    const confirmSessionFn = httpsCallable(functions, 'confirmSession');
    
    const result = await confirmSessionFn({
      sessionId
    });

    return result.data;
  } catch (error) {
    console.error('Error confirming session:', error);
    throw error;
  }
};

export const cancelSessionWithRefund = async (sessionId) => {
  try {
    const cancelSessionFn = httpsCallable(functions, 'cancelSessionWithRefund');
    
    const result = await cancelSessionFn({
      sessionId
    });

    return result.data;
  } catch (error) {
    console.error('Error cancelling session:', error);
    throw error;
  }
};

export const processProviderPayout = async (payoutType) => {
  try {
    const processPayoutFn = httpsCallable(functions, 'processProviderPayout');
    
    const result = await processPayoutFn({
      payoutType
    });

    return result.data;
  } catch (error) {
    console.error('Error processing payout:', error);
    throw error;
  }
};

export const sendMessageNotificationEmail = async (recipientId, messageData) => {
  try {
    const sendMessageNotificationFn = httpsCallable(functions, 'sendMessageNotificationEmail');
    
    const result = await sendMessageNotificationFn({
      recipientId,
      messageData
    });

    return result.data;
  } catch (error) {
    console.error('Error sending message notification email:', error);
    throw error;
  }
};

export default stripePromise;